import React, { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { PACKAGE_CATEGORIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PackageSearch, MapPin, ArrowRight, Weight, Search, Loader2, Package, ShieldCheck, Wallet } from "lucide-react";

export default function FindDeliveries() {
  const [filters, setFilters] = useState({ pickup: "", destination: "", date: "", category: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offering, setOffering] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/deliveries", { params });
      setItems(data.deliveries);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const submitOffer = async () => {
    setSending(true);
    try {
      await api.post("/connections", { delivery_id: offering.id, message: msg });
      toast.success("Your offer has been sent.");
      setOffering(null); setMsg("");
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="Find Deliveries" subtitle="Browse package requests you could carry along your route." />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="find-deliveries-filters">
        <div className="space-y-1.5"><Label className="text-xs">Pickup</Label><Input data-testid="fd-pickup" value={filters.pickup} onChange={(e) => setFilters((f) => ({ ...f, pickup: e.target.value }))} placeholder="From" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Destination</Label><Input data-testid="fd-destination" value={filters.destination} onChange={(e) => setFilters((f) => ({ ...f, destination: e.target.value }))} placeholder="To" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" data-testid="fd-date" value={filters.date} onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))} className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Category</Label>
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="fd-category" className="rounded-xl"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any category</SelectItem>{PACKAGE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={load} data-testid="fd-search-btn" className="rounded-xl h-10"><Search className="w-4 h-4" /> Search</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={PackageSearch} title="No delivery requests found." description="Try adjusting your filters or check back later." testid="empty-find-deliveries" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="find-deliveries-results">
          {items.map((d) => (
            <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col" data-testid={`delivery-card-${d.id}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-accent rounded-full px-2.5 py-1"><Package className="w-3.5 h-3.5" /> {d.category}</span>
                <StatusBadge status={d.status} />
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-800">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" /><span className="truncate">{d.pickup}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{d.destination}</span>
              </div>
              <p className="mt-2 text-sm text-slate-600 line-clamp-2">{d.description}</p>
              <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Weight className="w-3.5 h-3.5" /> {d.weight}</span>
                <span>{d.size}</span>
                <span>{d.delivery_date}</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-primary font-semibold">{d.sender?.name?.[0] || "?"}</span>
                {d.sender?.name}
                {d.sender?.verification_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />}
              </div>
              {d.price_amount != null && <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid={`earn-${d.id}`}><Wallet className="w-4 h-4" /> Earn ₹{d.price_amount} · Cash on Delivery</div>}
              <Button onClick={() => setOffering(d)} data-testid={`offer-to-deliver-${d.id}`} className="mt-4 rounded-xl w-full">Offer to Deliver</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!offering} onOpenChange={(o) => !o && setOffering(null)}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader><DialogTitle>Offer to deliver</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Send an offer to carry <strong>{offering?.description}</strong> from {offering?.pickup} to {offering?.destination}. The sender can accept or decline.</p>
          <Textarea data-testid="offer-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Add a short message (optional)" className="rounded-xl" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOffering(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitOffer} disabled={sending} data-testid="offer-submit-btn" className="rounded-xl">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send offer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
